'use client';

import { HelperTextSlot, IndicatorSlot, LabelSlot } from '@magicschool/spellbook/components/ProgressBar/slots';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import type { SpellbookNameOverrideProps } from '@magicschool/spellbook/lib/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { useDirection } from '@radix-ui/react-direction';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, FC } from 'react';

export type ProgressBarProps = ComponentProps<typeof ProgressPrimitive.Root> &
  VariantProps<typeof progressBarContainerVariants> &
  SpellbookNameOverrideProps;

const progressBarContainerVariants = cva('w-full flex items-center', {
  variants: {
    /** The visual appearance of the progress bar. */
    variant: {
      default: 'gap-2 flex-wrap',
      inline: 'gap-4',
    },
  },
});

const progressBarVariants = cva('bg-border-default relative h-2 w-full overflow-hidden rounded-full', {
  variants: {
    variant: {
      default: 'order-1',
      inline: '',
    },
  },
});

const ProgressBarBase: FC<ProgressBarProps> = ({
  children,
  className,
  value,
  variant = 'default',
  id: providedId,
  'data-spellbook-name': spellbookName,
  ...props
}) => {
  const ariaLabel = props['aria-label'] || 'Progress %';
  const id = useId(providedId);
  const { slots } = useSlots(children, {
    label: { Component: LabelSlot, props: { id, progressBarVariant: variant } },
    helperText: { Component: HelperTextSlot },
    indicator: { Component: IndicatorSlot, props: { value, variant } },
  });
  // TODO: Determine which direction provider we want to rely on: Radix or other
  const dir = useDirection();
  const translateValue = dir === 'rtl' ? 100 - (value || 0) : -100 + (value || 0);

  return (
    <div
      data-spellbook-name="progress-bar"
      {...(spellbookName && { 'data-spellbook-name': spellbookName })}
      className={cn('w-full flex flex-col gap-2', className)}
    >
      <div className={progressBarContainerVariants({ variant })}>
        {slots.label}
        <ProgressPrimitive.Root
          aria-label={slots.label ? undefined : ariaLabel}
          aria-labelledby={slots.label ? id : undefined}
          aria-valuetext={props['aria-valuetext']}
          className={cn(progressBarVariants({ variant }))}
          data-spellbook-name="progress"
          value={value}
          {...props}
        >
          <ProgressPrimitive.Indicator
            data-spellbook-name="progress-indicator"
            className="bg-primary-stroke h-full w-full flex-1 transition-all rounded-full"
            style={{ transform: `translateX(${translateValue}%)` }}
          />
        </ProgressPrimitive.Root>
        {slots.indicator}
      </div>
      {slots.helperText}
    </div>
  );
};

export const ProgressBar = Object.assign(ProgressBarBase, {
  Label: LabelSlot,
  HelperText: HelperTextSlot,
  Indicator: IndicatorSlot,
});
